import React, { useEffect, useMemo, useState } from "react";
import { LuUnplug } from "react-icons/lu";
import { toast } from "react-toastify";

import * as devConfig from "../../../app/configs/devConfig";
import { Card } from "../../../components/card/Card";
import { SettingsConfirmModal } from "../../../components/modal/SettingsConfirmModal";
import { useAppSelector } from "../../../redux/redux";
import { getLinkedFirm } from "../../../sessionStorage/sessionStorage";
import {
  useLazyDisconnectWithQboQuery,
  useLazyGetSyncDataQuery,
  useStartSyncMutation,
  useUpdateSyncDataMutation,
} from "../../../slices/QboSlice";
import QBO_Logo from "../../../utils/qbo-logo.png";

export const IntegrationsView = () => {
  const { user } = useAppSelector((state) => state.appReducer);

  const linkedFirm = getLinkedFirm();
  const orgId = linkedFirm?.orgId ?? "";
  const userId = user?.id ?? "";

  const [isDisconnectModalOpen, setIsDisconnectModalOpen] = useState(false);
  const [isSyncStarting, setIsSyncStarting] = useState(false);

  const [
    getSyncDataInfo,
    { data: syncDataInfo, isFetching: isFetchingSyncInfo },
  ] = useLazyGetSyncDataQuery();
  const [startSync] = useStartSyncMutation();
  const [updateSyncData] = useUpdateSyncDataMutation();
  const [disconnectWithQbo, { isFetching: isDisconnecting }] =
    useLazyDisconnectWithQboQuery();

  const isConnected = Boolean(syncDataInfo?.connectWithQBO);

  const qboConnectUrl = useMemo(() => {
    if (!orgId || !userId) return "";
    return `${devConfig.getServerBaseUrlForEnv()}/qbo/connect?userId=${userId.toUpperCase()}&orgId=${orgId.toUpperCase()}`;
  }, [orgId, userId]);

  useEffect(() => {
    if (!orgId) return;

    void getSyncDataInfo({ orgId });
  }, [getSyncDataInfo, orgId]);

  const handleConnect = () => {
    if (!qboConnectUrl) return;
    window.open(qboConnectUrl);
  };

  const handleSync = async () => {
    if (!orgId || !user?.id || !syncDataInfo?.id || isSyncStarting) return;

    try {
      setIsSyncStarting(true);

      await startSync({
        orgId,
        userId: user.id,
      }).unwrap();

      await updateSyncData({
        id: syncDataInfo.id,
        syncCustomer: true,
        syncServiceItems: true,
        syncPayments: true,
        syncInvoice: true,
        connectWithQbo: true,
        isSynced: true,
      }).unwrap();

      await getSyncDataInfo({ orgId }).unwrap();

      toast("Sync started successfully", {
        type: "success",
      });
    } catch (error) {
      console.error("Sync failed", error);

      toast("Sync failed. Please try again.", {
        type: "error",
      });
    } finally {
      setIsSyncStarting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!orgId || !userId) return;

    try {
      await disconnectWithQbo({
        orgId,
        userId,
      }).unwrap();

      await getSyncDataInfo({ orgId }).unwrap();
      setIsDisconnectModalOpen(false);
    } catch (error) {
      console.error("Failed to disconnect QBO integration:", error);
    }
  };

  return (
    <>
      <div className={"mt-[84px] flex gap-4"}>
        <Card
          extraClasses={
            "shadow-box flex h-[450px] w-[300px] !max-w-[540px] flex-col rounded-[6px] pb-4"
          }>
          <div className={"flex h-full flex-col"}>
            <div
              className={
                "flex flex-1 flex-col items-center justify-center gap-2"
              }>
              <div className={"flex w-full justify-center"}>
                <img src={QBO_Logo} width={"40%"} alt={"QuickBooks Online"} />
              </div>

              <div
                className={
                  "my-4 flex w-full flex-col items-center justify-center"
                }>
                <div className={"text-[16px] font-semibold"}>
                  QuickBooks Online
                </div>
                <div
                  className={
                    "flex w-[90%] flex-wrap justify-center text-center text-[12px]"
                  }>
                  Sync your clients, invoices and payments and track time
                </div>
              </div>
            </div>

            <div className={"flex justify-center"}>
              {!isConnected ? (
                <button
                  type={"button"}
                  onClick={handleConnect}
                  disabled={!qboConnectUrl || isFetchingSyncInfo}
                  className={
                    "flex w-[150px] justify-center rounded-lg bg-[#7c66f0] px-4 py-2 font-Manrope text-base font-semibold text-white disabled:opacity-75"
                  }>
                  Connect
                </button>
              ) : (
                <div className={"flex w-[300px] justify-center gap-2"}>
                  <button
                    type={"button"}
                    onClick={handleSync}
                    className={
                      "flex w-[100px] justify-center rounded-lg bg-[#7c66f0] px-4 py-2 font-Manrope text-sm font-semibold text-white disabled:opacity-75"
                    }>
                    {isSyncStarting ? "Syncing..." : "Sync"}
                  </button>

                  <button
                    type={"button"}
                    onClick={() => setIsDisconnectModalOpen(true)}
                    disabled={isDisconnecting}
                    className={
                      "flex w-[100px] justify-center rounded-lg bg-[#7c66f0] px-4 py-2 font-Manrope text-sm font-semibold text-white disabled:opacity-75"
                    }>
                    Disconnect
                  </button>
                </div>
              )}
            </div>
          </div>
        </Card>

        <Card
          extraClasses={
            "shadow-box flex h-[450px] !max-w-[540px] items-center justify-center rounded-[6px]"
          }>
          <div className={"flex items-center justify-center font-bold"}>
            More Coming Soon!
          </div>
        </Card>
      </div>

      {isDisconnectModalOpen && (
        <SettingsConfirmModal
          onClose={() => setIsDisconnectModalOpen(false)}
          onConfirm={handleDisconnect}
          isLoading={isDisconnecting}
          icon={<LuUnplug size={20} />}
          title={"Disconnect integration?"}
          description={
            "Are you sure you want to disconnect your QBO integration?<br />Once disconnected, your data will no longer sync. You can reconnect at any time."
          }
          confirmText={"Disconnect"}
          variant={"danger"}
        />
      )}
    </>
  );
};
